<template>
  <div
    v-if="showPaymentWarningChip || showComplimentaryWarningChip"
    class="biz-sub-banner"
    :class="bannerClass"
  >
    <i :class="bannerIcon" class="biz-sub-banner-icon"></i>
    <span class="biz-sub-banner-text">{{ bannerText }}</span>
    <button type="button" class="biz-sub-banner-btn" @click="redirectToBilling">
      {{ bannerCta }}
    </button>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { authSet } from "@/core/services/auth-storage";

/**
 * Global subscription warning for the business header (every page).
 * Subheader is disabled for business theme, so this lives in the top navbar.
 */
export default {
  name: "SubscriptionStatusBanner",
  data() {
    return {
      subscriptionTick: 0,
      refreshingSubscription: false,
    };
  },
  computed: {
    subscription() {
      void this.subscriptionTick;
      return this.$getSubscription();
    },

    isAdminRoute() {
      const path = this.$route.path || "";
      return path.includes("/admin/") || path.includes("/admin");
    },

    planName() {
      return this.subscription?.plan?.name || "Free";
    },

    isFreePlan() {
      return this.planName === "Free";
    },

    isInGracePeriod() {
      if (!this.subscription || this.isFreePlan) return false;
      return this.subscription.status === "grace_period";
    },

    isExpiredSubscription() {
      if (!this.subscription) return false;
      return (
        this.subscription.status === "expired" ||
        this.subscription.status === "canceled"
      );
    },

    isComplimentary() {
      return !!(this.subscription && this.subscription.is_complimentary);
    },

    hasHealthyPaidAccess() {
      const sub = this.subscription;
      if (!sub || this.isFreePlan) return true;
      if (this.isComplimentary && sub.status === "active") return true;
      return sub.status === "active";
    },

    showPaymentWarningChip() {
      return !this.hasHealthyPaidAccess && !this.isAdminRoute;
    },

    showComplimentaryWarningChip() {
      if (this.isAdminRoute || this.showPaymentWarningChip) return false;
      const sub = this.subscription;
      if (!sub || !sub.is_complimentary || this.isFreePlan) return false;
      if (sub.status !== "active") return false;
      const endRaw = sub.complimentary_ends_at || sub.end_date;
      if (!endRaw) return true;
      const end = new Date(endRaw);
      if (Number.isNaN(end.getTime())) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return Math.round((end - today) / 86400000) <= 14;
    },

    bannerClass() {
      if (this.showComplimentaryWarningChip) return "is-info";
      if (this.isExpiredSubscription) return "is-expired";
      if (this.isInGracePeriod) return "is-grace";
      return "is-warn";
    },

    bannerIcon() {
      if (this.showComplimentaryWarningChip) return "fas fa-gift";
      return "fas fa-exclamation-circle";
    },

    bannerText() {
      if (this.showComplimentaryWarningChip) {
        return this.complimentaryMessage;
      }
      return this.paymentMessage;
    },

    bannerCta() {
      if (this.showComplimentaryWarningChip) return "Subscribe";
      if (this.isInGracePeriod) return "Update payment";
      if (this.isExpiredSubscription) return "Renew now";
      return "Renew now";
    },

    complimentaryMessage() {
      const endRaw =
        this.subscription?.complimentary_ends_at || this.subscription?.end_date;
      if (!endRaw) {
        return "Complimentary Start ending soon — subscribe to keep access.";
      }
      return `Complimentary Start ends ${this.formatDate(endRaw)} — subscribe to keep access.`;
    },

    paymentMessage() {
      const sub = this.subscription;
      if (!sub) return "Your subscription needs renewal.";

      if (sub.status === "expired") {
        return "Plan expired — renew to restore full access.";
      }
      if (sub.status === "canceled") {
        return "Subscription canceled — renew to restore access.";
      }
      if (sub.status === "grace_period") {
        if (sub.grace_period_start) {
          const end = new Date(sub.grace_period_start);
          end.setDate(end.getDate() + 5);
          return `Payment failed — renew by ${this.formatDate(end)} to avoid deactivation.`;
        }
        return "Payment failed — renew soon to avoid deactivation.";
      }
      if (sub.end_date) {
        const endDate = new Date(sub.end_date);
        if (endDate < new Date()) {
          return "Plan period ended — renew to restore access.";
        }
      }
      return "Your subscription needs renewal.";
    },
  },
  watch: {
    "$route.fullPath"() {
      this.refreshSubscriptionQuietly();
    },
  },
  mounted() {
    this.refreshSubscriptionQuietly();
  },
  methods: {
    redirectToBilling() {
      this.$redirectToBilling();
    },
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    async refreshSubscriptionQuietly() {
      if (this.isAdminRoute || this.refreshingSubscription) return;
      const existing = this.$getSubscription();
      if (!existing || !existing.id) return;

      this.refreshingSubscription = true;
      try {
        const { data } = await ApiService.get("business/settings", "plans");
        if (data && data.subscription) {
          authSet("subscription", JSON.stringify(data.subscription));
          this.subscriptionTick += 1;
        }
      } catch (e) {
        // ignore — keep cached
      } finally {
        this.refreshingSubscription = false;
      }
    },
  },
};
</script>

<style scoped>
.biz-sub-banner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: min(560px, 46vw);
  margin-left: 12px;
  padding: 6px 10px 6px 12px;
  border-radius: 999px;
  border: 1px solid #fdba74;
  background: #fff7ed;
  flex-shrink: 1;
  min-width: 0;
}

.biz-sub-banner.is-grace {
  background: #fff7ed;
  border-color: #fdba74;
}

.biz-sub-banner.is-expired {
  background: #fef2f2;
  border-color: #fecaca;
}

.biz-sub-banner.is-info {
  background: #eff6ff;
  border-color: #93c5fd;
}

.biz-sub-banner-icon {
  font-size: 13px;
  color: #ea580c;
  flex-shrink: 0;
}

.biz-sub-banner.is-expired .biz-sub-banner-icon {
  color: #dc2626;
}

.biz-sub-banner.is-info .biz-sub-banner-icon {
  color: #2563eb;
}

.biz-sub-banner-text {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.biz-sub-banner-btn {
  flex-shrink: 0;
  border: none;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  background: #241b5a;
  color: #fff;
  white-space: nowrap;
}

.biz-sub-banner.is-expired .biz-sub-banner-btn {
  background: #dc2626;
}

.biz-sub-banner.is-grace .biz-sub-banner-btn {
  background: #ea580c;
}

.biz-sub-banner.is-info .biz-sub-banner-btn {
  background: #2563eb;
}

@media (max-width: 1100px) {
  .biz-sub-banner {
    max-width: min(360px, 38vw);
  }

  .biz-sub-banner-text {
    max-width: 140px;
  }
}

@media (max-width: 767px) {
  .biz-sub-banner {
    max-width: none;
    margin-left: 8px;
  }

  .biz-sub-banner-text {
    display: none;
  }
}
</style>
